import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { Newspaper, Star, ExternalLink, Bot, Award, TrendingUp } from "lucide-react";
import { SITE } from "../data/seoPages";

const ET_URL = "https://economictimes.indiatimes.com/ai-innovator/profile/brandborn/133204259";

export default function EconomicTimesPage() {
  return (
    <main className="bg-gray-950 text-white min-h-screen pt-28 pb-20 px-4" data-testid="et-press-page">
      <Helmet>
        <title>BrandBorn Featured in The Economic Times — ET AI Innovator Nominee | EcomCatalog.ai</title>
        <meta name="description" content="BrandBorn, India's #1 marketplace onboarding agency, has been nominated by The Economic Times as an ET AI Innovator for EcomCatalog.ai — India's first AI listing software. Read the official Economic Times feature on Brand Born." />
        <link rel="canonical" href={`${SITE}/economic-times`} />
        <meta property="og:title" content="BrandBorn in The Economic Times — ET AI Innovator Nominee" />
        <meta property="og:description" content="The Economic Times features BrandBorn and EcomCatalog.ai as an AI Innovator — the onboarding agency behind 1000+ successful marketplace launches." />
        <meta property="og:url" content={`${SITE}/economic-times`} />
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "NewsArticle",
          "headline": "BrandBorn nominated as ET AI Innovator by The Economic Times",
          "description": "The Economic Times has featured BrandBorn and its AI listing software EcomCatalog.ai in the ET AI Innovator program, recognising the onboarding agency behind 1000+ successful marketplace launches.",
          "publisher": { "@type": "Organization", "name": "The Economic Times", "url": "https://economictimes.indiatimes.com" },
          "about": { "@type": "Organization", "name": "BrandBorn", "url": SITE, "sameAs": [ET_URL] },
          "mainEntityOfPage": ET_URL,
          "url": `${SITE}/economic-times`
        })}</script>
      </Helmet>

      <div className="max-w-4xl mx-auto">
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-red-600 to-orange-500 rounded-full text-xs font-black uppercase tracking-wider mb-6">
            <Star size={14} className="text-yellow-300 fill-yellow-300" /> ET AI Innovator Nominee
          </div>
          <div className="bg-white rounded-2xl p-6 max-w-md mx-auto mb-8 shadow-2xl">
            <img src="/images/logos/economic-times.png" alt="The Economic Times features BrandBorn" className="w-full h-20 object-contain" />
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black mb-4">
            BrandBorn in <span className="bg-gradient-to-r from-red-500 via-orange-400 to-yellow-400 bg-clip-text text-transparent">The Economic Times</span>
          </h1>
          <p className="text-gray-400 text-base md:text-lg max-w-2xl mx-auto">
            India's #1 business newspaper has nominated Brand Born as an <strong className="text-white">ET AI Innovator</strong> for EcomCatalog.ai — India's first AI listing software.
          </p>
          <a href={ET_URL} target="_blank" rel="noopener noreferrer" data-testid="et-official-link"
            className="mt-8 inline-flex items-center gap-2 px-8 py-4 bg-gradient-to-r from-red-600 to-orange-500 hover:from-red-500 hover:to-orange-400 rounded-full font-black uppercase tracking-wider text-sm shadow-lg shadow-red-900/40 transition-colors">
            <Newspaper size={16} /> Read the Official ET Feature <ExternalLink size={14} />
          </a>
        </div>

        <blockquote className="bg-white/[0.06] backdrop-blur-xl border border-white/10 rounded-3xl p-8 md:p-10 text-center mb-12">
          <p className="text-xl md:text-2xl italic text-gray-200 leading-relaxed">"The onboarding agency behind 1000+ successful marketplace launches."</p>
          <footer className="mt-4 text-sm text-gray-500 font-bold uppercase tracking-wider">— The Economic Times, ET AI Innovator</footer>
        </blockquote>

        <div className="grid md:grid-cols-3 gap-6 mb-14">
          {[
            { icon: Award, title: "ET AI Innovator", desc: "Brand Born has been officially nominated in The Economic Times' AI Innovator program, recognising India's most impactful AI-driven companies." },
            { icon: Bot, title: "EcomCatalog.ai", desc: "The Economic Times feature highlights EcomCatalog.ai — India's first AI listing software, built in-house by BrandBorn to generate marketplace-ready catalogs in minutes." },
            { icon: TrendingUp, title: "1000+ Launches", desc: "The nomination recognises BrandBorn's track record: 1000+ brands launched across Myntra, Amazon, Flipkart, Nykaa, Ajio, Blinkit and 70+ marketplaces." },
          ].map((c) => (
            <div key={c.title} className="bg-white/[0.05] border border-white/10 rounded-2xl p-6">
              <c.icon size={26} className="text-orange-400 mb-4" />
              <h2 className="font-black text-lg mb-2">{c.title}</h2>
              <p className="text-gray-400 text-sm leading-relaxed">{c.desc}</p>
            </div>
          ))}
        </div>

        <div className="prose prose-invert max-w-none text-gray-300 text-sm md:text-base leading-relaxed space-y-4 mb-14">
          <h2 className="text-2xl font-black text-white">Why The Economic Times featured Brand Born</h2>
          <p>
            The Economic Times — India's #1 business newspaper — profiles Brand Born (BrandBorn) in its <a href={ET_URL} target="_blank" rel="noopener noreferrer" className="text-orange-400 underline">AI Innovator program</a>. The feature covers how BrandBorn combined deep marketplace onboarding expertise with in-house AI technology, <strong className="text-white">EcomCatalog.ai</strong>, to help Indian D2C brands launch on Myntra, Amazon, Flipkart, Nykaa, Ajio, Zepto, Blinkit and 70+ marketplaces.
          </p>
          <p>
            As an Official Myntra OMS Partner with a 98% first-time approval rate and a 14-day standard launch timeline, BrandBorn's recognition by The Economic Times adds to earlier national media coverage in The Times of India.
          </p>
        </div>

        <div className="text-center">
          <Link to="/" className="inline-flex items-center gap-2 px-6 py-3 bg-white/10 hover:bg-white/20 border border-white/20 rounded-full text-sm font-bold transition-colors">← Back to BrandBorn</Link>
        </div>
      </div>
    </main>
  );
}
